import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { OrgInfo } from "@preveil-api";
import { Account, AdminErrorMessages, Message, MessageAnchors, MessageHandlerDisplayType, useAppDispatch, useAdminSetInviteEmailTypeMutation, usePatchUsersOrgsByEntityIdShowMuaPromptMutation, useGetUsersOrgsByEntityIdQuery, UserResultsMode } from "src/common";
import { AlertWithSwitch, CoverTemplate, PageHeader, Loading } from "src/components";
import { uiActions } from "src/store";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
};

// Description: Admin Settings - a place where admins can manage settings related to user onboarding, mail client prompts, and drive sync defaults.
function AdminSettingsComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const [user_onboarding, setUserOnboarding] = useState<boolean>(false);
  const [add_to_mail_client, setAddToMailClient] = useState<boolean>(false);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [adminSetInviteEmailType] = useAdminSetInviteEmailTypeMutation();
  const [patchUsersOrgsByEntityIdShowMuaPrompt] = usePatchUsersOrgsByEntityIdShowMuaPromptMutation();
  const { data: org_info_data, error, isSuccess } = useGetUsersOrgsByEntityIdQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: {
      entity_id: org_info.org_id,
      user_results: UserResultsMode.none
    }
  });

  useEffect(() => {
    if (isSuccess && org_info_data) {
      const { no_download_email, show_mua_prompt } = org_info_data;
      setUserOnboarding(!no_download_email);
      setAddToMailClient(show_mua_prompt);
      setIsLoading(false);
    }
    if (error) {
      dispatchPageError({ message: AdminErrorMessages.error_admin_settings_data, stack: error });
      setIsLoading(false);
    }
  }, [org_info_data, error, isSuccess]);

  // Description: Handles Request for User Onboarding.
  async function toggleUserOnboardingRequest() {
    const { user_id } = current_account;
    // Only Crypto Endpoint the others are coming from Collection API.
    await adminSetInviteEmailType({
      userId: user_id,
      orgId: org_info.org_id,
      inviteEmailState: user_onboarding,
    }).unwrap().then(() => {
        setUserOnboarding(!user_onboarding);
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_admin_settings_switch_options.replace(MessageAnchors.message_content, "changing User Onboarding options"),
          stack: error
        };
        dispatchPageError(params);
      });
  };

  // Description: Handles Request for Add to Mail Client.
  async function toggleAddToMailClientPrompt() {
    const show_prompt = !add_to_mail_client;
    await patchUsersOrgsByEntityIdShowMuaPrompt({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        entityId: org_info.org_id,
        show_prompt
      }
    }).unwrap().then(() => {
      setAddToMailClient(show_prompt);
    })
    .catch((error) => {
      const failed_toggle_state = show_prompt ? "disabling Add to Mail Client prompt" : "enabling Add to Mail Client prompt";
      const params = {
        message: AdminErrorMessages.error_admin_settings_switch_options.replace(MessageAnchors.message_content, failed_toggle_state),
        stack: error
      };
      dispatchPageError(params);
    });
  };

  function dispatchPageError(params: { message: string, stack?: any }) {
    dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
  }

  return <CoverTemplate>
    <PageHeader>
      <h1>Admin Settings</h1>
    </PageHeader>
    {is_loading ? <Loading /> :
      <Card className="card-section">
        <h5>User Onboarding</h5>
        <AlertWithSwitch
          popover={true}
          toggleState={user_onboarding}
          handleChange={toggleUserOnboardingRequest}
          popover_message="Disabling this option will remove the prompts asking members to install PreVeil. Installation link should be disabled when PreVeil is deploying through Active Directory or other remote deployment tools."
        >
          <span>
            Onboarding emails <strong>{`${!user_onboarding ? "won't" : "will"}`}</strong>{" "}
            include installation link.
          </span>
        </AlertWithSwitch>

        <h5>Mail Client Prompt</h5>
        <AlertWithSwitch
          popover={true}
          toggleState={add_to_mail_client}
          handleChange={toggleAddToMailClientPrompt}
          popover_message="Disabling this option will remove the Mail Client prompt upon claiming an account."
        >
          <span>
            New users <strong>{`${add_to_mail_client ? "will" : "will not"}`}</strong> be prompted to install email client integration.
          </span>
        </AlertWithSwitch>
      </Card>
    }
  </CoverTemplate>;
}

export default React.memo(AdminSettingsComponent);
